import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Trans } from "react-i18next";
import { useRevalidator } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { setBrowseLocationId, useIsAnonymousBrowsing } from "@/modules/anonymousBrowsing";
import { getPlatformLocation, getPlatformLocations, getUser } from "api/methods";
import LocationPinIcon from "atoms/icon/icons/location-pin.svg";
import { ModalSelectorList } from "organisms/selection-modals/modal-selector-list/ModalSelectorList";
import {
  invalidateShopForLocationChange,
  useSwitchPlatformLocation,
} from "modules/platformLocation";

export const ShopLocationBanner = () => {
  const { t } = useTranslation();
  const { revalidate } = useRevalidator();
  const isAnonymousBrowsing = useIsAnonymousBrowsing();
  const { data: user } = useQuery({ ...getUser.keyed() });
  const { data: locations } = useQuery({
    ...getPlatformLocations.keyed({}),
    select: ({ items }) => items,
  });
  // A visitor has no PlatformUser to read a home location from. `GET /platform-location` answers anonymously and
  // already honours the browse-location header, so it reports whichever location the visitor is currently browsing
  // (the practice default until they pick one).
  const { data: browseLocation } = useQuery({
    ...getPlatformLocation.keyed(),
    enabled: isAnonymousBrowsing,
  });

  const { switchLocation, isSwitching } = useSwitchPlatformLocation();
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  const currentLocationId = isAnonymousBrowsing ? browseLocation?.id : user?.platformLocation?.id;
  const currentLocationName = isAnonymousBrowsing
    ? browseLocation?.name
    : user?.platformLocation?.name;

  // The banner is only meaningful when there is a current location to switch away from and the platform offers at
  // least one alternative active location. Both hold for a visitor once the practice allows anonymous browsing.
  if (!currentLocationId || !currentLocationName || !locations || locations.length < 2) {
    return null;
  }

  const openPicker = () => {
    setSelectedLocationId(currentLocationId);
    setErrorMessage(undefined);
    setIsPickerOpen(true);
  };

  const changeLocation = async () => {
    if (!selectedLocationId || selectedLocationId === currentLocationId) {
      setIsPickerOpen(false);
      return;
    }
    setErrorMessage(undefined);
    if (isAnonymousBrowsing) {
      // No account to persist against: the choice rides on the browse-location header for the rest of the session
      // and is handed to /auth/verify if the visitor checks out, so the account starts where they were shopping.
      setBrowseLocationId(selectedLocationId);
      await invalidateShopForLocationChange();
      // The index reads the invalidated keys directly and refetches on its own; the loader still
      // owns the marketplace check and the shop's other pages.
      revalidate();
      setIsPickerOpen(false);
      return;
    }

    if (await switchLocation(selectedLocationId)) {
      setIsPickerOpen(false);
      return;
    }

    // Keep the picker open and surface the failure rather than silently closing.
    setErrorMessage(t("shopLocationBanner.errorSwitchLocation"));
  };

  return (
    <>
      <div className="flex items-center justify-between gap-2 px-4 py-1.5">
        <div className="flex min-w-0 items-center gap-1">
          <LocationPinIcon className="shrink-0 fill-light-grey" width="14" height="14" />
          <span className="truncate text-body3 text-secondary">
            <Trans
              i18nKey="shopLocationBanner.browsingLocation"
              values={{ locationName: currentLocationName }}
              components={{ bold: <span className="font-medium" /> }}
            />
          </span>
        </div>
        <button
          type="button"
          className="shrink-0 text-body3 font-medium text-secondary"
          onClick={openPicker}
        >
          {t("shopLocationBanner.change")}
        </button>
      </div>
      <ModalSelectorList
        size="medium"
        open={isPickerOpen}
        title={t("shopLocationBanner.chooseALocation")}
        type="location"
        items={locations
          .map((location) => ({
            id: location.id,
            title: location.name ?? "",
            description: `${location.city}, ${location.state}, ${location.postalCode}`,
            subtitle: location.address1 ?? undefined,
          }))
          .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }))}
        checkedItems={[selectedLocationId]}
        errorMessage={errorMessage}
        displayError={!!errorMessage}
        onClose={() => {
          setIsPickerOpen(false);
          setSelectedLocationId(currentLocationId);
          setErrorMessage(undefined);
        }}
        firstButtonLabel={t("shopLocationBanner.viewThisLocation")}
        firstButtonDisabled={
          isSwitching || !selectedLocationId || selectedLocationId === currentLocationId
        }
        onFirstButtonClick={changeLocation}
        onSelect={(id) => {
          setSelectedLocationId(id);
          setErrorMessage(undefined);
        }}
      />
    </>
  );
};
